import React from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { exportTableCsv } from '@/lib/riqueExport';

export type RiqueVisualization =
  | {
      type: 'kpi';
      title?: string;
      items: Array<{ label: string; value: string | number }>;
    }
  | {
      type: 'bar';
      title: string;
      items: Array<{ name: string; value: number }>;
    }
  | {
      type: 'table';
      title: string;
      columns: string[];
      rows: string[][];
    };

export type RiqueDeepLink = { label: string; href: string };

export type RiqueActionDraft = {
  tipo: 'email' | 'whatsapp';
  clienteNome: string;
  draft: string;
  aviso?: string;
};

interface RiqueRichContentProps {
  visualizations?: RiqueVisualization[];
  deepLinks?: RiqueDeepLink[];
  actionDraft?: RiqueActionDraft;
  compact?: boolean;
  expanded?: boolean;
}

export const RiqueRichContent: React.FC<RiqueRichContentProps> = ({
  visualizations = [],
  deepLinks = [],
  actionDraft,
  compact = false,
  expanded = false,
}) => {
  const copyDraft = async () => {
    if (!actionDraft?.draft) return;
    await navigator.clipboard.writeText(actionDraft.draft);
  };

  if (visualizations.length === 0 && deepLinks.length === 0 && !actionDraft) return null;

  return (
    <div className={cn('mt-2 space-y-2', compact && !expanded ? 'max-w-[88%]' : expanded ? 'max-w-[85%]' : 'max-w-xl')}>
      {visualizations.map((viz, idx) => {
        if (viz.type === 'kpi') {
          return (
            <div key={idx} className="rounded-lg border border-border/70 bg-background/80 p-2">
              {viz.title ? (
                <p className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">{viz.title}</p>
              ) : null}
              <div className={cn('grid gap-1.5', expanded ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2')}>
                {viz.items.map((item) => (
                  <div key={item.label} className="rounded bg-muted/40 px-2 py-1">
                    <p className="text-[10px] text-muted-foreground">{item.label}</p>
                    <p className="text-xs font-semibold text-foreground">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (viz.type === 'bar' && viz.items.length > 0) {
          return (
            <div key={idx} className="rounded-lg border border-border/70 bg-background/80 p-2">
              <p className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">{viz.title}</p>
              <div className={cn('w-full', expanded ? 'h-48' : 'h-28')}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={viz.items} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={40} />
                    <YAxis tick={{ fontSize: 9 }} width={32} />
                    <Tooltip formatter={(v: number) => v.toLocaleString('pt-BR')} />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        }

        if (viz.type === 'table') {
          const rowLimit = expanded ? 20 : 6;
          return (
            <div key={idx} className="overflow-x-auto rounded-lg border border-border/70 bg-background/80 p-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-[10px] font-semibold uppercase text-muted-foreground">{viz.title}</p>
                <button
                  type="button"
                  onClick={() => exportTableCsv(viz.title, viz.columns, viz.rows)}
                  className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium hover:bg-muted/60"
                  title="Exportar CSV"
                >
                  <Download className="h-3 w-3" aria-hidden />
                  CSV
                </button>
              </div>
              <table className={cn('w-full', expanded ? 'text-xs' : 'text-[10px]')}>
                <thead>
                  <tr className="text-left text-muted-foreground">
                    {viz.columns.map((col) => (
                      <th key={col} className="px-1 py-0.5 font-medium">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {viz.rows.slice(0, rowLimit).map((row, rowIdx) => (
                    <tr key={rowIdx} className="border-t border-border/50">
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className="px-1 py-0.5 text-foreground">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {viz.rows.length > rowLimit ? (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  +{viz.rows.length - rowLimit} linhas — expanda a conversa ou exporte CSV
                </p>
              ) : null}
            </div>
          );
        }

        return null;
      })}

      {actionDraft ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2">
          <p className="text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-400">
            Rascunho {actionDraft.tipo === 'whatsapp' ? 'WhatsApp' : 'E-mail'} — {actionDraft.clienteNome}
          </p>
          <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap text-[10px] text-foreground">
            {actionDraft.draft}
          </pre>
          {actionDraft.aviso ? <p className="mt-1 text-[10px] text-muted-foreground">{actionDraft.aviso}</p> : null}
          <button
            type="button"
            onClick={() => void copyDraft()}
            className="mt-1.5 rounded border border-border px-2 py-0.5 text-[10px] font-medium hover:bg-muted/60"
          >
            Copiar rascunho
          </button>
        </div>
      ) : null}

      {deepLinks.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {deepLinks.map((link) => (
            <Link
              key={link.href + link.label}
              to={link.href}
              className="rounded-full border border-border/80 px-2 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/5"
            >
              {link.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
};
